/**
 * B-24b WS-3 — Platform health status banner for the Operator Console.
 *
 * Echoes the WORST signal from GET /admin/platform-health onto the front
 * door without requiring the SA to navigate to PlatformHealth.tsx.
 *
 * Rendering rules:
 *   - green + monitoring configured → calm "Platform healthy" (positive)
 *   - green + monitoring NOT configured → muted "Monitoring not configured"
 *     info state (text-tertiary, NO amber alarm chrome, NO "Review →"). This is
 *     the P0-2 fix: an unconfigured monitor is not a degraded platform (founder
 *     ruling 2026-06-08).
 *   - yellow → amber warning with signal summary (a REAL upstream degradation)
 *   - red → brand-negative alert with a "Review →" link to /platform-health
 *   - loading → narrow skeleton (no layout shift)
 *   - error → silent (banner omitted; SA can navigate to Health manually)
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchPlatformHealth, type PlatformHealthResponse } from "@/lib/dashboardApi";

const STATUS_STYLES: Record<
  PlatformHealthResponse["status_banner"],
  { container: string; dot: string; label: string; text: string }
> = {
  green: {
    container: "border-brand-positive/30 bg-brand-positive/[0.06]",
    dot: "bg-brand-positive",
    label: "Platform healthy",
    text: "text-brand-positive",
  },
  yellow: {
    container: "border-brand-warn/40 bg-brand-warn/[0.08]",
    dot: "bg-brand-warn",
    label: "Platform degraded",
    text: "text-brand-warn",
  },
  red: {
    container: "border-brand-negative/40 bg-brand-negative/[0.08]",
    dot: "bg-brand-negative",
    label: "Platform incident",
    text: "text-brand-negative",
  },
};

export function OperatorStatusBanner() {
  const { data, isLoading } = useQuery({
    queryKey: ["platform-health"],
    queryFn: fetchPlatformHealth,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <div
        data-testid="status-banner-loading"
        className="h-8 w-full animate-pulse rounded-lg bg-surface-canvas"
      />
    );
  }

  if (!data) {
    // Error or undefined — render nothing; SA navigates manually if needed
    return null;
  }

  // P0-2: green banner + monitoring unconfigured → a calm muted info state, NOT
  // a degraded alarm. Distinct chrome: no border tint, no colored dot, no link.
  if (data.status_banner === "green" && data.monitoring_configured === false) {
    return (
      <div
        data-testid="operator-status-banner"
        data-status="green"
        data-monitoring="unconfigured"
        className="flex items-center gap-2.5 rounded-lg border border-border-default bg-surface-card px-4 py-2"
      >
        <span className="h-2 w-2 rounded-full bg-text-disabled" aria-hidden="true" />
        <span className="text-xs font-medium text-text-tertiary">
          Monitoring not configured
        </span>
        <span className="text-2xs text-text-tertiary">
          · uptime &amp; error tracking aren't wired yet
        </span>
      </div>
    );
  }

  const s = STATUS_STYLES[data.status_banner];
  const isAlert = data.status_banner !== "green";

  return (
    <div
      data-testid="operator-status-banner"
      data-status={data.status_banner}
      className={
        "flex items-center justify-between rounded-lg border px-4 py-2 " +
        s.container
      }
    >
      <div className="flex items-center gap-2.5">
        <span className={"h-2 w-2 rounded-full " + s.dot} />
        <span className={"text-xs font-semibold " + s.text}>{s.label}</span>
        {isAlert && data.recent_exceptions.status === "ok" && data.recent_exceptions.groups.length > 0 && (
          <span className="text-2xs text-text-tertiary">
            · {data.recent_exceptions.groups[0]?.title ?? "see health page"}
          </span>
        )}
      </div>
      {isAlert && (
        <Link
          to="/platform-health"
          className={"text-2xs font-bold uppercase tracking-[0.12em] hover:underline " + s.text}
        >
          Review →
        </Link>
      )}
    </div>
  );
}
